import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Eye, ArrowLeft, FileText, Star, Calendar, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BLOG_CATEGORIES = [
  'Buying Guide',
  'Expert Advice',
  'Planning Guide',
  'Workplace Wellness',
  'Cost Management',
  'Strategic Planning',
  'Industry News',
  'Case Studies'
];

export default function AdminBlog() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'Buying Guide',
    author: 'MFS Team',
    image_url: '',
    read_time: '5 min read',
    featured: false,
    published: true
  });

  useEffect(() => {
    // Wait for auth to finish loading before checking user
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }
    if (!user.is_admin) {
      navigate('/');
      toast.error('Admin access required');
      return;
    }
    fetchPosts();
  }, [user, authLoading, navigate]);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/blog`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to fetch blog posts');
    } finally {
      setLoading(false);
    }
  };

  const seedPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/blog/seed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      toast.success(data.message);
      fetchPosts();
    } catch (error) {
      toast.error('Failed to seed posts');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingPost 
        ? `${API_URL}/api/admin/blog/${editingPost.id}`
        : `${API_URL}/api/admin/blog`;
      
      const response = await fetch(url, {
        method: editingPost ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingPost ? 'Blog post updated!' : 'Blog post created!');
        setIsDialogOpen(false);
        resetForm();
        fetchPosts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save post');
      }
    } catch (error) {
      toast.error('Failed to save blog post');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/blog/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Blog post deleted');
        fetchPosts();
      } else {
        toast.error('Failed to delete post');
      }
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const openEditDialog = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      author: post.author,
      image_url: post.image_url,
      read_time: post.read_time,
      featured: post.featured,
      published: post.published
    });
    setIsDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingPost(null);
    resetForm();
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: 'Buying Guide',
      author: 'MFS Team',
      image_url: '',
      read_time: '5 min read',
      featured: false,
      published: true
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen noise-texture">
        <Navigation />
        <div className="container mx-auto px-8 py-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground uppercase tracking-wider">
            {authLoading ? 'Verifying access...' : 'Loading posts...'}
          </p>
        </div>
      </div>
    );
  }

  if (!user || !user.is_admin) {
    return null;
  }

  return (
    <div className="min-h-screen noise-texture">
      <Navigation />
      
      <div className="container mx-auto px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
            <h1 className="text-3xl font-black uppercase flex items-center gap-3">
              <FileText className="w-8 h-8 text-primary" />
              BLOG MANAGEMENT
            </h1>
            <p className="text-muted-foreground mt-1">Create and manage blog posts for SEO & authority building</p>
          </div>
          <div className="flex gap-3">
            {posts.length === 0 && (
              <Button onClick={seedPosts} variant="outline">
                Seed Initial Posts
              </Button>
            )}
            <Button onClick={openCreateDialog} className="bg-primary">
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border border-border p-4 rounded-lg">
            <p className="text-sm text-muted-foreground">Total Posts</p>
            <p className="text-3xl font-bold">{posts.length}</p>
          </div>
          <div className="bg-white border border-border p-4 rounded-lg">
            <p className="text-sm text-muted-foreground">Published</p>
            <p className="text-3xl font-bold text-green-600">{posts.filter(p => p.published).length}</p>
          </div>
          <div className="bg-white border border-border p-4 rounded-lg">
            <p className="text-sm text-muted-foreground">Drafts</p>
            <p className="text-3xl font-bold text-yellow-600">{posts.filter(p => !p.published).length}</p>
          </div>
          <div className="bg-white border border-border p-4 rounded-lg">
            <p className="text-sm text-muted-foreground">Featured</p>
            <p className="text-3xl font-bold text-primary">{posts.filter(p => p.featured).length}</p>
          </div>
        </div>

        {/* Posts List */}
        <div className="bg-white border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-4 font-bold text-sm uppercase">Title</th>
                  <th className="text-left p-4 font-bold text-sm uppercase">Category</th>
                  <th className="text-left p-4 font-bold text-sm uppercase">Status</th>
                  <th className="text-left p-4 font-bold text-sm uppercase">Featured</th>
                  <th className="text-left p-4 font-bold text-sm uppercase">Date</th>
                  <th className="text-left p-4 font-bold text-sm uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => (
                  <tr key={post.id} className="border-t border-border hover:bg-muted/30">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <img 
                          src={post.image_url} 
                          alt={post.title}
                          className="w-16 h-12 object-cover rounded"
                        />
                        <div>
                          <p className="font-semibold line-clamp-1">{post.title}</p>
                          <p className="text-xs text-muted-foreground">/blog/{post.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="bg-primary/10 text-primary px-2 py-1 rounded text-xs font-medium">
                        {post.category}
                      </span>
                    </td>
                    <td className="p-4">
                      {post.published ? (
                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
                          Published
                        </span>
                      ) : (
                        <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs font-medium">
                          Draft
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      {post.featured && (
                        <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                      )}
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {new Date(post.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <a 
                          href={`/blog/${post.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-muted rounded"
                          title="View"
                        >
                          <Eye className="w-4 h-4 text-muted-foreground" />
                        </a>
                        <button
                          onClick={() => openEditDialog(post)}
                          className="p-2 hover:bg-muted rounded"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4 text-blue-600" />
                        </button>
                        <button
                          onClick={() => handleDelete(post.id)}
                          className="p-2 hover:bg-muted rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {posts.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No blog posts yet</p>
                      <p className="text-sm">Click "Seed Initial Posts" to add starter content</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black uppercase">
              {editingPost ? 'Edit Blog Post' : 'Create New Blog Post'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold">Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Enter blog post title"
                  required
                />
              </div>
              <div>
                <Label className="text-sm font-bold">Slug (auto-generated if empty)</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({...formData, slug: e.target.value})}
                  placeholder="url-friendly-slug"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm font-bold">Category *</Label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full border border-border rounded-md p-2"
                  required
                >
                  {BLOG_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-sm font-bold">Author</Label>
                <Input
                  value={formData.author}
                  onChange={(e) => setFormData({...formData, author: e.target.value})}
                  placeholder="MFS Team"
                />
              </div>
              <div>
                <Label className="text-sm font-bold">Read Time</Label>
                <Input
                  value={formData.read_time}
                  onChange={(e) => setFormData({...formData, read_time: e.target.value})}
                  placeholder="5 min read"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-bold">Featured Image URL *</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                placeholder="https://images.unsplash.com/..."
                required
              />
              {formData.image_url && (
                <img 
                  src={formData.image_url} 
                  alt="Preview" 
                  className="mt-2 w-full h-40 object-cover rounded"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
            </div>

            <div>
              <Label className="text-sm font-bold">Excerpt * (Short description for listing)</Label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                placeholder="Brief description of the blog post..."
                rows={2}
                required
              />
            </div>

            <div>
              <Label className="text-sm font-bold">Content * (Supports Markdown)</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                placeholder="## Heading&#10;&#10;Write your blog content here...&#10;&#10;- Bullet point&#10;- Another point&#10;&#10;**Bold text**"
                rows={15}
                className="font-mono text-sm"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Use ## for headings, ### for subheadings, **text** for bold, - for bullet points
              </p>
            </div>

            <div className="flex items-center gap-8">
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.published}
                  onCheckedChange={(checked) => setFormData({...formData, published: checked})}
                />
                <Label>Published</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.featured}
                  onCheckedChange={(checked) => setFormData({...formData, featured: checked})}
                />
                <Label>Featured Post</Label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-primary">
                {editingPost ? 'Update Post' : 'Create Post'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
